
function sanitizeToFragment(dirtyHtml) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(dirtyHtml ?? ""), "text/html");

    // Allow only the tags you explicitly want to render.
    const ALLOWED = new Set([
        "P",
        "UL",
        "OL",
        "LI",
        "STRONG",
        "EM",
        "BR",
        "A",
        "BLOCKQUOTE",
        "CODE",
        "H3",
        "H4",
        "IMG",
    ]);

    // Allow only a minimal set of attributes per tag.
    const ALLOWED_ATTR = {
        A: new Set(["href", "title", "target"]),
        IMG: new Set(["src", "alt", "title", "width", "height", "loading", "decoding"]),
    };

    function isSafeHttpUrl(url) {
        // For IMG src: allow only http(s) and relative URLs.
        try {
            const u = new URL(url, window.location.origin);
            return ["http:", "https:"].includes(u.protocol);
        } catch {
            return false;
        }
    }

    function isSafeLinkUrl(url) {
        // For A href: allow http(s), mailto, tel, and relative URLs.
        try {
            const u = new URL(url, window.location.origin);
            return ["http:", "https:", "mailto:", "tel:"].includes(u.protocol);
        } catch {
            return false;
        }
    }

    function isSafeDimension(value) {
        // Avoid weird values; accept only digits.
        return /^[0-9]{1,5}$/.test(String(value));
    }

    const frag = document.createDocumentFragment();

    function walk(node, outParent) {
        if (node.nodeType === Node.TEXT_NODE) {
            outParent.appendChild(document.createTextNode(node.nodeValue));
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const tag = node.tagName.toUpperCase();

        // If tag isn't allowed, unwrap it: keep walking its children but drop the element itself.
        if (!ALLOWED.has(tag)) {
            for (const child of node.childNodes) walk(child, outParent);
            return;
        }

        const el = document.createElement(tag.toLowerCase());

        // Copy only allowed attributes with additional validation.
        const allowedAttrs = ALLOWED_ATTR[tag];
        if (allowedAttrs) {
            for (const attr of node.attributes) {
                const name = attr.name.toLowerCase();
                const value = attr.value;

                if (!allowedAttrs.has(name)) continue;

                if (tag === "A" && name === "href") {
                    // Blocks javascript:, data:, etc.
                    if (!isSafeLinkUrl(value)) continue;
                    el.setAttribute("href", value);
                    continue;
                }

                if (tag === "A" && name === "target") {
                    // Optional: only allow _blank
                    if (value === "_blank") el.setAttribute("target", "_blank");
                    continue;
                }

                if (tag === "IMG" && name === "src") {
                    // Blocks data:, blob:, file:, etc.
                    if (!isSafeHttpUrl(value)) continue;
                    el.setAttribute("src", value);
                    continue;
                }

                if (tag === "IMG" && (name === "width" || name === "height")) {
                    if (!isSafeDimension(value)) continue;
                    el.setAttribute(name, value);
                    continue;
                }

                if (tag === "IMG" && name === "loading") {
                    if (value === "lazy" || value === "eager") el.setAttribute("loading", value);
                    continue;
                }

                if (tag === "IMG" && name === "decoding") {
                    if (value === "async" || value === "auto" || value === "sync") el.setAttribute("decoding", value);
                    continue;
                }

                // alt/title or any other allowed attribute without special rules
                el.setAttribute(name, value);
            }

            // Prevent tabnabbing for target="_blank" links.
            if (tag === "A" && el.getAttribute("target") === "_blank") {
                el.setAttribute("rel", "noopener noreferrer");
            }
        }

        for (const child of node.childNodes) walk(child, el);
        outParent.appendChild(el);
    }

    for (const child of doc.body.childNodes) walk(child, frag);
    return frag;
}

export async function faq() {
    const filters = document.querySelectorAll(".faq-filters.desktop div.filter");
    const allFilter = document.querySelector(".faq-filters.desktop div.filter");
    const mobileFilters = document.querySelectorAll(".faq-filters-mobile div.filter");
    const mobileAllFilter = document.querySelector(".faq-filters-mobile div.filter");
    const questionArea = document.querySelector("div#faq-items");
    const groups = document.querySelectorAll("div#faq-items .group");

    // Prefer span.title (original markup). If caller passes .item, we normalize in setExpand().
    const items = document.querySelectorAll(
        ".oportun-accordion .items .oportun-accordion-item:not(.menu) .item span.title"
    );

    const menu = document.querySelector(".oportun-accordion .items .oportun-accordion-item.menu .item .title");
    const loadedFilters = new URL(window.location.toString()).searchParams.get("filter");
    const currLang = document.documentElement.lang;

    if (items && items.length) {
        setExpand(items);
    }

    if (menu) {
        menu.addEventListener("click", expand);
        menu.addEventListener("keydown", keyExpand);
    }

    if (filters) {
        filters.forEach((filter) => {
            filter.addEventListener("click", filterQuestions);
            filter.addEventListener("keydown", keyFilterQuestions);
        });

        mobileFilters.forEach((filter) => {
            filter.addEventListener("click", filterQuestions);
            filter.addEventListener("keydown", keyFilterQuestions);
        });
    }

    if (loadedFilters) {
        updateFilters(loadedFilters);
    }

    window.addEventListener("popstate", function () {
        const currFilters = new URL(window.location.toString()).searchParams.get("filter");
        resetFilters();
        if (currFilters) {
            updateFilters(currFilters);
        } else {
            updateAllFilter(true);
            getFilterString();
        }
    });

    function keyFilterQuestions(e) {
        if (e.key === "Enter") {
            filterQuestions(e);
        } else if (e.key === " ") {
            e.preventDefault();
            filterQuestions(e);
        }
    }

    function filterQuestions(e) {
        // Use currentTarget for robustness (avoid depending on bubbling target).
        const filter = e.currentTarget;

        if (!filter.classList.contains("active")) {
            if (filter !== allFilter && filter !== mobileAllFilter) {
                updateAllFilter(false);
            } else {
                resetFilters();
                updateAllFilter(true);
            }
            filter.classList.remove("inactive");
            filter.classList.add("active");
            filter.setAttribute("aria-checked", "true");
        } else {
            if (filter !== allFilter && filter !== mobileAllFilter) {
                filter.classList.remove("active");
                filter.classList.add("inactive");
                filter.setAttribute("aria-checked", "false");
            }
        }

        getFilterString();
    }

    function updateFilters(queryString) {
        const loadedFiltersArray = queryString.split(",");
        loadedFiltersArray.forEach((filter) => {
            // Escape filter value since it comes from URL params.
            const currFilter = document.querySelector(`[data-filter="${CSS.escape(filter)}"]`);
            if (!currFilter) return;
            currFilter.classList.remove("inactive");
            currFilter.classList.add("active");
            currFilter.setAttribute("aria-checked", "true");
            updateAllFilter(false);
        });

        getFilterString();
    }

    function getFilterString() {
        let activeFilters = null;
        if (isMobile()) {
            activeFilters = document.querySelectorAll(".faq-filters-mobile div.filter.active");
        } else {
            activeFilters = document.querySelectorAll(".faq-filters.desktop div.filter.active");
        }

        const filterMsg = document.querySelector("div.faq-active-filters");

        const filteredQuestions = document.querySelector("#filtered-items");
        if (filteredQuestions) {
            filteredQuestions.remove();
        }

        if (
            activeFilters.length > 0 &&
            !(activeFilters.length == 1 && (activeFilters[0] === allFilter || activeFilters[0] === mobileAllFilter))
        ) {
            let filterArray = [];
            let currentURL = window.location.origin + window.location.pathname;
            let queryString = "";

            activeFilters.forEach((filter) => {
                filterArray.push(filter.dataset.filter);
                if (!queryString) {
                    queryString = "?filter=" + filter.dataset.filter;
                } else {
                    queryString += "," + filter.dataset.filter;
                }
            });

            window.history.pushState({}, "", currentURL + queryString);

            // Build heading content safely using DOM APIs instead of innerHTML
            filterMsg.innerHTML = "";
            const heading = document.createElement("h3");

            const questionText = currLang === "es-ES" ? "Preguntas relacionadas con " : "Questions related to ";
            const concatenator = currLang === "es-ES" ? " y " : " and ";
            heading.appendChild(document.createTextNode(questionText));

            activeFilters.forEach((filter, index) => {
                const span = document.createElement("span");
                span.classList.add("faq-filter");
                span.textContent = filter.textContent;
                heading.appendChild(span);

                if (index < activeFilters.length - 2) {
                    heading.appendChild(document.createTextNode(", "));
                } else if (index === activeFilters.length - 2) {
                    heading.appendChild(document.createTextNode(concatenator));
                }
            });

            filterMsg.appendChild(heading);
            filterMsg.classList.add("show");

            if (filterArray) {
                filterFAQ(filterArray);
            }
        } else {
            filterMsg.classList.remove("show");
            updateAllFilter(true);

            groups.forEach((group) => {
                group.style.display = "block";
            });

            // Re-bind accordion after reset
            const currItems = document.querySelectorAll(
                ".oportun-accordion .items .oportun-accordion-item:not(.menu) .item span.title"
            );
            setExpand(currItems);

            window.history.pushState({}, "", window.location.origin + window.location.pathname);
        }
    }

    function filterFAQ(filtersArr) {
        groups.forEach((group) => {
            group.style.display = "none";
        });

        let filteredFAQ = document.createElement("div");
        filteredFAQ.id = "filtered-items";

        let questionsDiv = document.createElement("div");
        questionsDiv.classList.add("questions");

        let accordionDiv = document.createElement("div");
        accordionDiv.classList.add("oportun-accordion");

        let itemsDiv = document.createElement("div");
        itemsDiv.classList.add("items");

        let itemsAdded = false;

        window.faqs.questions.forEach((question) => {
            let add = null;
            let categories = question.post_categories.split(",");

            filtersArr.forEach((filter) => {
                if (filter && categories.includes(filter) && (add == null || add == true)) {
                    add = true;
                } else {
                    add = false;
                }
            });

            if (add) {
                const itemWrapper = document.createElement("div");
                itemWrapper.classList.add("oportun-accordion-item");

                const itemDiv = document.createElement("div");
                itemDiv.classList.add("item");

                const titleSpan = document.createElement("span");
                titleSpan.classList.add("title");
                // tabindex/role/aria added in applyAccordionA11y()

                const titleDiv = document.createElement("div");
                titleDiv.classList.add("title");
                // Use textContent to avoid injecting HTML in titles.
                titleDiv.textContent = question.title;

                const icon = document.createElement("oportun-icon");
                icon.setAttribute("name", "positive");
                icon.setAttribute("aria-hidden", "true");

                const contentDiv = document.createElement("div");
                contentDiv.classList.add("content");
                contentDiv.style.maxHeight = "0px";
                contentDiv.style.visibility = "hidden";

                const contentSpan = document.createElement("span");
                contentSpan.classList.add("answer");

                // XSS hardening:
                // - Do NOT assign question.content via innerHTML.
                // - Instead, parse and rebuild only allowlisted tags/attributes.
                contentSpan.replaceChildren(sanitizeToFragment(question.content));

                contentDiv.appendChild(contentSpan);
                titleSpan.appendChild(titleDiv);
                titleSpan.appendChild(icon);

                // IMPORTANT: content is sibling of titleSpan (matches original markup)
                itemDiv.appendChild(titleSpan);
                itemDiv.appendChild(contentDiv);

                itemWrapper.appendChild(itemDiv);
                itemsDiv.appendChild(itemWrapper);

                itemsAdded = true;
            }
        });

        if (itemsAdded) {
            accordionDiv.appendChild(itemsDiv);
            questionsDiv.appendChild(accordionDiv);
            filteredFAQ.appendChild(questionsDiv);
            questionArea.appendChild(filteredFAQ);

            const newItems = document.querySelectorAll(
                "#filtered-items .oportun-accordion .items .oportun-accordion-item:not(.menu) .item span.title"
            );
            setExpand(newItems);
        } else {
            const noMatchParagraph = document.createElement("p");
            const strong = document.createElement("strong");
            strong.textContent =
                currLang === "es-ES"
                    ? "No se encontraron coincidencias para su selección."
                    : "No matches were found for your selection.";
            noMatchParagraph.appendChild(strong);
            filteredFAQ.appendChild(noMatchParagraph);
            questionArea.appendChild(filteredFAQ);
        }
    }

    // A11y + robustness: always resolve the real clickable/focusable header element
    function expand(e) {
        const title = e.target.closest("span.title");
        if (!title) return;

        const item = title.closest(".item");
        if (!item) return;

        const description = item.querySelector("div.content");
        const icon = title.querySelector("oportun-icon");
        const expanded = document.querySelector("span.title.expanded");

        if (expanded && expanded !== title) {
            const expandedItem = expanded.closest(".item");
            const expandedDescription = expandedItem ? expandedItem.querySelector("div.content") : null;
            const expandedIcon = expanded.querySelector("oportun-icon");

            expanded.classList.remove("expanded");
            expanded.setAttribute("aria-expanded", "false");

            if (expandedDescription) {
                expandedDescription.style.maxHeight = "0px";
                expandedDescription.style.visibility = "hidden";
            }
            if (expandedIcon) expandedIcon.setAttribute("name", "positive");
        }

        if (!title.classList.contains("expanded")) {
            title.classList.add("expanded");
            title.setAttribute("aria-expanded", "true");

            if (description) {
                description.style.maxHeight = description.scrollHeight + "px";
                description.style.visibility = "visible";
            }

            if (icon) {
                if (!title.closest(".menu .item")) {
                    icon.setAttribute("name", "negative");
                } else {
                    icon.setAttribute("name", "arrow-up");
                }
            }
        } else {
            title.classList.remove("expanded");
            title.setAttribute("aria-expanded", "false");

            if (description) {
                description.style.maxHeight = "0px";
                description.style.visibility = "hidden";
            }

            if (icon) {
                if (!title.closest(".menu .item")) {
                    icon.setAttribute("name", "positive");
                } else {
                    icon.setAttribute("name", "arrow-down");
                }
            }
        }
    }

    function resetFilters() {
        filters.forEach((filter) => {
            filter.classList.remove("active");
            filter.classList.add("inactive");
            filter.setAttribute("aria-checked", "false");
        });

        mobileFilters.forEach((filter) => {
            filter.classList.remove("active");
            filter.classList.add("inactive");
            filter.setAttribute("aria-checked", "false");
        });
    }

    function updateAllFilter(active) {
        if (active) {
            allFilter.classList.remove("inactive");
            allFilter.classList.add("active");
            allFilter.setAttribute("aria-checked", "true");
            mobileAllFilter.classList.remove("inactive");
            mobileAllFilter.classList.add("active");
            mobileAllFilter.setAttribute("aria-checked", "true");
        } else {
            allFilter.classList.remove("active");
            allFilter.classList.add("inactive");
            allFilter.setAttribute("aria-checked", "false");
            mobileAllFilter.classList.remove("active");
            mobileAllFilter.classList.add("inactive");
            mobileAllFilter.setAttribute("aria-checked", "false");
        }
    }

    function replaceLastChar(originalString, targetChar, replacementChar) {
        const lastIndex = originalString.lastIndexOf(targetChar);
        if (lastIndex === -1) return originalString;

        return (
            originalString.substring(0, lastIndex) +
            replacementChar +
            originalString.substring(lastIndex + 1)
        );
    }

    function keyExpand(e) {
        if (e.key === "Enter") {
            expand(e);
        } else if (e.key === " ") {
            e.preventDefault();
            expand(e);
        }
    }

    function applyAccordionA11y(titleEl, index, scopeId) {
        // Ensure titleEl is the focusable header
        titleEl.setAttribute("role", "button");
        titleEl.setAttribute("tabindex", "0");

        // Give the header a stable id (used by the panel aria-labelledby)
        if (!titleEl.id) titleEl.id = `faq_acc_btn_${scopeId}_${index}`;

        // Label: use the visible title text element if present
        const labelEl = titleEl.querySelector("div.title");
        if (labelEl) {
            if (!labelEl.id) labelEl.id = `faq_acc_lbl_${scopeId}_${index}`;
            titleEl.setAttribute("aria-labelledby", labelEl.id);
        } else {
            // fallback
            titleEl.setAttribute("aria-label", titleEl.textContent.trim());
        }

        const item = titleEl.closest(".item");
        const panel = item ? item.querySelector("div.content") : null;

        if (panel) {
            if (!panel.id) panel.id = `faq_acc_panel_${scopeId}_${index}`;
            titleEl.setAttribute("aria-controls", panel.id);

            // Initial expanded state based on class (and visibility if already set)
            const isExpanded = titleEl.classList.contains("expanded") && panel.style.visibility !== "hidden";
            titleEl.setAttribute("aria-expanded", isExpanded ? "true" : "false");

            panel.setAttribute("role", "region");
            panel.setAttribute("aria-labelledby", titleEl.id);
        } else {
            // still set a conservative default
            if (!titleEl.hasAttribute("aria-expanded")) titleEl.setAttribute("aria-expanded", "false");
        }

        // Icon should not be in the accessibility tree
        const icon = titleEl.querySelector("oportun-icon");
        if (icon) icon.setAttribute("aria-hidden", "true");
    }

    function setExpand(updatedItems) {
        // scopeId prevents id collisions when items are rebuilt (e.g., filtered list)
        const scopeId = (document.querySelector("#filtered-items") && "filtered") || "base";

        updatedItems.forEach((node, idx) => {
            // Normalize: allow passing either .item or span.title
            const title =
                node.matches && node.matches("span.title")
                    ? node
                    : node.querySelector && node.querySelector("span.title");
            if (!title) return;

            // Apply ARIA semantics once (safe to re-apply)
            applyAccordionA11y(title, idx, scopeId);

            title.addEventListener("click", expand);
            title.addEventListener("keydown", keyExpand);
        });
    }

    function isMobile() {
        return window.innerWidth < 860;
    }
}